import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useVirtualizer } from '@tanstack/react-virtual';
import { HealthStatusIndicator } from '@/components/shared/display/StateIndicator';
import { BaseCard } from '@/components/uiKit/Cards';
import { Card } from '@/components/ui/card';
import { REGULAR_DATE_FORMAT } from '@/const';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { HEALTH_STATUS } from '@/types';
import { PlayIcon } from '@/assets/Icons';
import { Icon } from '@/components/shared/display/Icon';
import { VideoOffIcon } from 'lucide-react';

interface EvidencePlaylistProps {
  selectedEvidence: any;
  onPlaylistItemClick: (id: string) => void;
}

export const EvidencePlaylist = ({ selectedEvidence, onPlaylistItemClick }: EvidencePlaylistProps) => {
  const playlistRef = useRef<HTMLDivElement>(null);
  const { visualAIPlaylistItems } = useSelector((state: RootState) => state.assetsStudio);

  // Virtual scrolling - only render visible items for better performance
  const virtualizer = useVirtualizer({
    count: visualAIPlaylistItems?.length || 0,
    getScrollElement: () => playlistRef.current,
    estimateSize: () => 73, // 65px height + 8px margin
    overscan: 5, // Render 5 extra items above/below viewport
  });

  // Auto-scroll to selected evidence
  useEffect(() => {
    if (selectedEvidence && visualAIPlaylistItems) {
      const selectedIndex = visualAIPlaylistItems.findIndex((item: any) => item.uniqueId === selectedEvidence);
      if (selectedIndex > -1) {
        virtualizer.scrollToIndex(selectedIndex, { align: 'center', behavior: 'smooth' });
      }
    }
  }, [selectedEvidence, visualAIPlaylistItems, virtualizer]);

  if (!visualAIPlaylistItems) return null;

  return (
    <BaseCard
      variant="wrapper"
      classNames={{
        card: 'h-full w-1/4',
        title: 'h-[30px]',
        content: `h-[calc(100%-32px)] max-h-[calc(100%-30px)] bg-gray-200 flex flex-col px-0 gap-2 py-0 overflow-y-auto pt-[2px] flex flex-col ${
          visualAIPlaylistItems?.length === 0 ? 'justify-center' : ''
        }`,
      }}
      title={
        <div className="flex justify-between w-full items-center">
          <div className="flex gap-1 items-center">Component Evidence</div>
        </div>
      }
    >
      <div ref={playlistRef} className="overflow-y-auto">
        {visualAIPlaylistItems.length === 0 ? (
          <EmptyEvidenceState />
        ) : (
          <div
            style={{
              height: `${virtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {virtualizer.getVirtualItems().map((virtualItem) => {
              const el = visualAIPlaylistItems[virtualItem.index];
              const isSelected = el.uniqueId === selectedEvidence;
              return (
                <div
                  key={el.uniqueId}
                  data-index={virtualItem.index}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                  className="flex w-full items-center gap-2"
                  data-testid="evidence_card"
                  data-is_selected={isSelected}
                >
                  <Card
                    data-key={el.uniqueId}
                    className={cn(
                      'w-full h-[65px] flex justify-between px-4 py-3 items-center gap-2 mb-2 hover:ring-2 ring-inset ring-primary-500 hover:cursor-pointer border-none',
                      isSelected ? 'ring-2 ring-primary-600' : ''
                    )}
                    onClick={() => {
                      onPlaylistItemClick(el.uniqueId);
                    }}
                  >
                    <div className="flex flex-col gap-1">
                      <div className={cn('text-sm capitalize font-semibold')}>{`${
                        el.fault_type
                          ? el.fault_type
                          : el.health_status === HEALTH_STATUS.HEALTHY
                          ? 'No Fault Detected'
                          : 'Fault Detected'
                      }`}</div>
                      <div className="flex gap-2  items-center">
                        <HealthStatusIndicator type={el.health_status} />
                        <div className="text-xs">{format(new Date(el.timestamp), REGULAR_DATE_FORMAT)}</div>
                      </div>
                    </div>
                    <Icon icon={PlayIcon} className="w-8 h-8" />
                  </Card>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </BaseCard>
  );
};

const EmptyEvidenceState = () => {
  const { t } = useTranslation();
  return (
    <div className="h-full w-full flex flex-col items-center justify-center gap-1">
      <VideoOffIcon size={24} className="text-primary-400" />
      <div className="text-sm p-2 pt-0">{t('visualAI.noEvidenceFoundForSelectedDateRange')}</div>
    </div>
  );
};
